// Reset a therapist's or rider's password — the fallback when their phone is
// lost or changed (Leon, 2026-10-17; docs/specs/staff-password-reset.md).
// Admin and Superadmin only; the admin Edge route enforces it and audits who,
// whose, when and how identity was checked.
//
// The temporary password is shown ONCE, here, and nowhere else: the server
// neither stores nor logs it. The staff member must replace it at next sign-in.

import { useState } from "react";
import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Field, FieldDescription, FieldLabel } from "@bednbody/ui/components/field";
import { Textarea } from "@bednbody/ui/components/textarea";
import { WarningIcon } from "@phosphor-icons/react";
import { adminResetStaffPasswordSchema } from "@bednbody/types";
import { toast } from "sonner";
import { useResetStaffPassword } from "../roster.hooks";

interface ResetPasswordDialogProps {
  staffId: string;
  staffName: string;
  /** An inactive account can be reset but cannot sign in until reactivated. */
  inactive?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ResetPasswordDialog({
  staffId,
  staffName,
  inactive = false,
  open,
  onOpenChange,
}: ResetPasswordDialogProps) {
  const reset = useResetStaffPassword();
  const [identityCheck, setIdentityCheck] = useState("");
  const [temporary, setTemporary] = useState<string | null>(null);
  const noteId = `identity-check-${staffId}`;
  const valid = adminResetStaffPasswordSchema.safeParse({ identityCheck }).success;

  function change(next: boolean) {
    // Forget the password the moment the dialog closes.
    if (!next) {
      setIdentityCheck("");
      setTemporary(null);
      reset.reset();
    }
    onOpenChange(next);
  }

  function submit() {
    if (!valid) return;
    reset.mutate(
      { staffId, identityCheck: identityCheck.trim() },
      {
        onSuccess: (data) => setTemporary(data.temporaryPassword),
        onError: (error) =>
          toast.error("Couldn't reset the password", {
            description: error instanceof Error ? error.message : undefined,
          }),
      },
    );
  }

  async function copy() {
    if (!temporary) return;
    try {
      await navigator.clipboard.writeText(temporary);
      toast.success("Copied");
    } catch {
      toast.error("Couldn't copy. Read it out instead.");
    }
  }

  return (
    <Dialog onOpenChange={change} open={open}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reset password · {staffName}</DialogTitle>
          <DialogDescription>
            {temporary
              ? "Give this temporary password to them directly. It is shown only once. They must choose a new one when they sign in."
              : "For a lost or changed phone. They are signed out everywhere and get a temporary password. Every reset is kept in the Audit Log."}
          </DialogDescription>
        </DialogHeader>

        {inactive ? (
          <p className="flex gap-2 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-warning">
            <WarningIcon weight="fill" className="mt-px size-4 shrink-0" />
            <span>
              {staffName} is inactive. The new password will not let them sign in until you make the
              account active again.
            </span>
          </p>
        ) : null}

        {temporary ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Temporary password</p>
            <p className="select-all rounded-md bg-muted px-3 py-2 font-mono text-lg tracking-wider text-foreground">
              {temporary}
            </p>
          </div>
        ) : (
          <Field>
            <FieldLabel htmlFor={noteId}>How did you check it's them?</FieldLabel>
            <Textarea
              id={noteId}
              maxLength={500}
              onChange={(event) => setIdentityCheck(event.target.value)}
              placeholder="e.g. Video call, matched the ID on file"
              required
              value={identityCheck}
            />
            <FieldDescription>Saved with the reset in the Audit Log.</FieldDescription>
          </Field>
        )}

        <DialogFooter>
          {temporary ? (
            <>
              <Button onClick={copy} variant="outline">
                Copy
              </Button>
              <Button onClick={() => change(false)}>Done</Button>
            </>
          ) : (
            <>
              <Button disabled={reset.isPending} onClick={() => change(false)} variant="outline">
                Cancel
              </Button>
              <Button disabled={!valid} loading={reset.isPending} onClick={submit}>
                Reset password
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
